import React, { useState, useEffect, useMemo } from 'react';
import { Card } from 'pokemon-tcg-sdk-typescript';
import { useCollectionStore } from '../../lib/collectionStore';
import CardGrid from '../../components/CardGrid';
import { useNavigate } from 'react-router-dom';
import { slugify } from '../../utils/slugify';
import { supabase } from '../../lib/supabase';
import { type CardFinish, getFinishOption } from '../../components/CollectionButton';
interface FinishCount {
  finish: CardFinish;
  count: number;
}

export default function TotalCardsView() {
  const { collection } = useCollectionStore();
  const navigate = useNavigate();
  const [selectedFinish, setSelectedFinish] = useState<CardFinish | null>(null);

  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;

  // Calculate finish counts from collection
  const finishCounts = collection.reduce<FinishCount[]>((acc, item) => {
    const existingFinish = acc.find(f => f.finish === item.finish);
    if (existingFinish) {
      existingFinish.count += item.quantity;
    } else {
      acc.push({ finish: item.finish, count: item.quantity });
    }
    return acc;
  }, []);

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchCards = async () => {
      try {
        if (!collection.length) {
          if (isMounted) {
            setCards([]);
            setLoading(false);
          }
          return;
        }

        if (isMounted) {
          setLoading(true);
          setError(null);
        }

        // Get unique card IDs
        const uniqueCardIds = Array.from(new Set(collection.map(item => item.cardId)));

        // Fetch cards from Supabase in batches to avoid query limits
        let allCardData = [];
        const batchSize = 1000;
        
        for (let i = 0; i < uniqueCardIds.length; i += batchSize) {
          const batchIds = uniqueCardIds.slice(i, i + batchSize);
          
          const { data: batchData, error: batchError } = await supabase
            .from('pokemontcg_io')
            .select(`
              *,
              set:pokemontcg_io_sets(*)
            `)
            .in('id', batchIds)
            .abortSignal(abortController.signal);
            
          if (batchError) {
            throw batchError;
          }
          
          if (batchData) {
            allCardData.push(...batchData);
          }
        }
        
        const cardData = allCardData;
        const cardError = null;

        if (!isMounted) return;

        if (cardError) {
          throw cardError;
        }

        if (!cardData) {
          throw new Error('No card data returned');
        }

        // Transform the cards
        const transformedCards = cardData.map(card => ({
          ...card,
          images: {
            small: card.image_small_url,
            large: card.image_large_url
          },
          set: {
            ...card.set,
            releaseDate: card.set.release_date,
            images: {
              symbol: card.set.symbol_url,
              logo: card.set.logo_url
            }
          }
        }));

        // Map collection quantities and filter by selected finish
        const filteredCollectionItems = selectedFinish 
          ? collection.filter(item => item.finish === selectedFinish)
          : collection;

        const cardsWithQuantities = transformedCards.flatMap(card => {
          const collectionItems = filteredCollectionItems.filter(item => item.cardId === card.id);
          return collectionItems.flatMap(item => 
            Array.from({ length: item.quantity }).map((_, index) => ({
              ...card,
              uniqueId: `${card.id}-${item.finish}-${Date.now()}-${index}`,
              finish: item.finish
            }))
          );
        });

        if (isMounted) {
          setCards(cardsWithQuantities);
          setLoading(false);
          setError(null);
        }
      } catch (error) {
        if (!isMounted) return;

        // Ignore AbortError as it's expected when component unmounts
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }

        console.error('Error fetching cards:', error);
        setError('Error loading cards. Please try again.');
        
        if (retryCount < MAX_RETRIES) {
          setTimeout(() => {
            if (isMounted) {
              setRetryCount(prev => prev + 1);
            }
          }, RETRY_DELAY);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCards();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [collection, retryCount, selectedFinish]);

  const handleCardClick = (card: Card) => {
    const seriesSlug = slugify(card.set.series);
    const setSlug = slugify(card.set.name);
    const cardSlug = `${slugify(card.name)}-${card.number}`;
    navigate(`/${seriesSlug}/${setSlug}/${cardSlug}`);
  };



  return (
    <div className="space-y-6">
      <div className="bg-black/95 border border-white/10 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold mb-2">All Cards</h2>
            <p className="text-white/60">
              Showing all cards in your collection, including duplicates.
            </p>
          </div>
          {cards.length > 0 && (
            <div className="text-sm text-white/60">
              {cards.length} cards total
            </div>
          )}
        </div>

        {/* Card Finishes Section */}
        <div className="mt-4">
          <h3 className="text-sm font-medium text-white/60 mb-2">Card Finishes</h3>
          <div className="flex flex-wrap gap-2">
            {finishCounts.map(({ finish, count }) => {
              const opt = getFinishOption(finish);
              return (
                <button
                  key={finish}
                  onClick={() => setSelectedFinish(selectedFinish === finish ? null : finish)}
                  className={`px-3 py-1.5 rounded-lg border transition-colors ${
                    selectedFinish === finish 
                      ? `${opt.color} bg-white/15 ring-2 ring-white/25` 
                      : `${opt.color} bg-white/5 opacity-75 hover:opacity-100`
                  } text-white text-sm flex items-center gap-2`}
                >
                  <span className={`w-2 h-2 rounded-full ${opt.dotColor}`} />
                  <span>{opt.label}</span>
                  <span className="bg-white/10 px-2 py-0.5 rounded-full text-xs">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-xs text-white/40 mt-2">
            Click a finish type to filter your collection.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
        </div>
      ) : cards.length === 0 ? (
        <div className="text-center py-12 text-white/60">
          {selectedFinish 
            ? `No ${getFinishOption(selectedFinish).label.toLowerCase()} cards in your collection.`
            : 'No cards in your collection yet.'
          }
        </div>
      ) : (
        <CardGrid
          key={`total-cards-${cards.length}-${selectedFinish || 'all'}`}
          cards={cards}
          onCardClick={handleCardClick}
          loading={loading}
          isCollectionView={true}
          enablePagination={true}
          cardsPerPage={250}
        />
      )}
    </div>
  );
}